import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

// Data for the terms sections - easier to manage and map over
const termsSections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: `By accessing or using the NRI Local Buddy ("NLB") platform and its services, you agree to comply with and be legally bound by these Terms and Conditions ("Terms") and our Privacy Policy. If you do not agree to these Terms, you have no right to obtain information from or otherwise continue using the platform.`,
  },
  {
    id: "services",
    title: "2. Scope of Services",
    content: `NLB provides a platform to connect Non-Resident Indians (NRIs) with local service providers for a variety of assistance services, including but not limited to property management, parental care, and travel coordination. The availability, scope, and pricing of services may vary by location and the specific service provider engaged.`,
  },
  {
    id: "user-responsibilities",
    title: "3. User Responsibilities",
    content: `You are responsible for providing accurate, current, and complete information during the registration and booking process. You agree to use the services for lawful purposes only and not to engage in any activity that is fraudulent, harassing, or otherwise illegal. Misuse of the platform may result in account suspension or termination and may be subject to legal action.`,
  },
  {
    id: "payments",
    title: "4. Payments, Cancellations & Refunds",
    content: (
      <>
        All payments for services must be made through our secure payment gateway. Prices are listed in the currency specified on the service page. Our refund and cancellation policies are specific to each service and will be clearly outlined before you complete your purchase. For any billing inquiries, please contact our support team at{" "}
        <a href="mailto:billing@nrilocalbuddy.com" className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
          billing@nrilocalbuddy.com
        </a>.
      </>
    ),
  },
  {
    id: "liability",
    title: "5. Limitation of Liability",
    content: `To the maximum extent permitted by law, NLB shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your access to or use of, or inability to access or use, the services. While we vet our service providers, we do not guarantee the quality, safety, or legality of the services provided.`,
  },
  {
    id: "intellectual-property",
    title: "6. Intellectual Property",
    content: `All content on the NLB platform, including text, graphics, logos, trademarks, and software, is the exclusive property of NLB or its licensors. You may not reproduce, distribute, modify, or create derivative works from any content without our express written consent.`,
  },
  {
    id: "governing-law",
    title: "7. Governing Law & Jurisdiction",
    content: `These Terms are governed by and construed in accordance with the laws of India, without regard to its conflict of law principles. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in Hyderabad, Telangana, India, and the parties irrevocably consent to the personal jurisdiction and venue therein.`,
  },
];

// Framer Motion Variants for animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState("acceptance");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for better accuracy

      for (const section of termsSections) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <section className="min-h-screen bg-slate-50 font-sans antialiased">
      <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Terms & Conditions
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
            Welcome to <strong>NRI Local Buddy (NLB)</strong>. Please review the following terms that govern your use of our platform.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Last Updated: October 26, 2023
          </p>
        </motion.div>

        <div className="relative lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sticky Navigation */}
          <aside className="hidden lg:block lg:col-span-3">
            <nav className="sticky top-24">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
                On this page
              </h3>
              <ul className="space-y-2">
                {termsSections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={`block text-sm font-medium transition-colors duration-200 ${
                        activeSection === section.id
                          ? "text-indigo-600 font-semibold"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9 bg-white p-8 sm:p-12 rounded-2xl shadow-sm border border-slate-200/80">
            <motion.div
              className="prose prose-slate max-w-none prose-a:text-indigo-600"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {termsSections.map((section) => (
                <motion.div
                  key={section.id}
                  id={section.id}
                  className="mb-10 scroll-mt-24"
                  variants={itemVariants}
                >
                  <h2 className="text-2xl font-bold tracking-tight text-slate-800 border-b border-slate-200 pb-3 mb-6">
                    {section.title}
                  </h2>
                  <div className="text-slate-700 leading-relaxed space-y-4">
                    <p>{section.content}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </main>
        </div>
      </div>
    </section>
  );
};

export default TermsAndConditions;